/**
 * Project API queries.
 */

import { apiClient } from "./client";

export interface Project {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
}

export async function fetchProjects(): Promise<Project[]> {
  return apiClient<Project[]>("/projects");
}

export async function fetchProject(id: string): Promise<Project> {
  return apiClient<Project>(`/projects/${id}`);
}

export async function resolveSlug(slug: string): Promise<string> {
  const res = await apiClient<{ id: string }>(`/projects/by-slug/${encodeURIComponent(slug)}`);
  return res.id;
}

export async function createProject(name: string): Promise<Project> {
  return apiClient<Project>("/projects", {
    method: "POST",
    body: { name },
  });
}

export async function updateProject(
  id: string,
  updates: { name?: string; description?: string },
): Promise<Project> {
  return apiClient<Project>(`/projects/${id}`, {
    method: "PATCH",
    body: updates,
  });
}

export async function deleteProject(id: string): Promise<void> {
  await apiClient(`/projects/${id}`, { method: "DELETE" });
}

export interface ReimportResult {
  project_id: string;
  nodes_created: number;
  cases_imported: number;
  cases_skipped: number;
}

export async function reimportProject(id: string): Promise<ReimportResult> {
  return apiClient<ReimportResult>(`/projects/${id}/reimport`, {
    method: "POST",
  });
}

export async function deleteNode(nodeId: string): Promise<void> {
  await apiClient(`/nodes/${nodeId}`, { method: "DELETE" });
}

export interface ProjectOverrides {
  ref_area?: number | null;
  ref_area_units?: string | null;
}

/** @deprecated Use fetchReferenceQuantities instead */
export async function fetchOverrides(projectId: string): Promise<ProjectOverrides> {
  return apiClient<ProjectOverrides>(`/projects/${projectId}/overrides`);
}

/** @deprecated Use setReferenceQuantity instead */
export async function updateOverrides(
  projectId: string,
  overrides: ProjectOverrides,
): Promise<ProjectOverrides> {
  return apiClient<ProjectOverrides>(`/projects/${projectId}/overrides`, {
    method: "PATCH",
    body: overrides,
  });
}

// ---------------------------------------------------------------------------
// Reference quantities (first-class nodes)
// ---------------------------------------------------------------------------

export interface ReferenceQuantity {
  id: string;
  path: string;
  value_scalar?: number | null;
  value_json?: Record<string, unknown> | null;
  units?: string | null;
  source?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string | null;
}

export interface SetReferenceQuantityResult {
  node_id: string;
  path: string;
  parameter: string;
  status: string;
}

export async function fetchReferenceQuantities(
  projectId: string,
  branch = "main",
): Promise<ReferenceQuantity[]> {
  // Backend #11187 changed response from bare list to {total, items}.
  // Accept both shapes for forward/backward compatibility.
  const raw = await apiClient<
    ReferenceQuantity[] | { total: number; items: ReferenceQuantity[] }
  >(
    `/projects/reference-quantities?project_id=${projectId}&branch=${encodeURIComponent(branch)}&limit=500`,
  );
  if (Array.isArray(raw)) return raw;
  return raw?.items ?? [];
}

export async function setReferenceQuantity(
  projectId: string,
  parameter: string,
  value: number | Record<string, unknown>,
  units?: string,
  source = "user_input",
  reason?: string,
  branch = "main",
): Promise<SetReferenceQuantityResult> {
  return apiClient<SetReferenceQuantityResult>("/projects/reference-quantities", {
    method: "POST",
    body: {
      project_id: projectId,
      parameter,
      value,
      units,
      source,
      reason,
      branch,
    },
  });
}

export async function deleteReferenceQuantity(
  parameter: string,
  projectId: string,
  branch = "main",
): Promise<{ parameter: string; status: string }> {
  return apiClient(`/projects/reference-quantities/${parameter}?project_id=${projectId}&branch=${encodeURIComponent(branch)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Surface meshes (DAG nodes with kind = 'surface_mesh')
// ---------------------------------------------------------------------------

export interface ProjectSurfaceMesh {
  node_id: string;
  name: string;
  path: string;
  flow360_surface_mesh_id: string;
  flow360_geometry_id: string;
}

export async function fetchProjectSurfaceMeshes(
  projectId: string,
  branch = "main",
): Promise<ProjectSurfaceMesh[]> {
  return apiClient<ProjectSurfaceMesh[]>(
    `/projects/${projectId}/surface-meshes?branch=${encodeURIComponent(branch)}`,
  );
}
